import styled, { css } from "styled-components";

export type ChangesTriggerTone = "pending" | "success" | "error";

/** `pending` reads `--orangeChipBackground` (a dedicated dim token — see `ColorProvider`) rather
 *  than the brighter `--orangeBackground` used for job-status badges/inline banners elsewhere;
 *  `success`/`error` reuse the shared green/red background tokens as-is since those are already
 *  soft, flat fills (Spotter's own nflOkSoft/nflDangerSoft hex). */
const TONE_STYLES = {
  pending: css`
    background: var(--orangeChipBackground);
    color: var(--orange400);
    border-color: var(--orangeBorder);
  `,
  success: css`
    background: var(--greenBackground);
    color: var(--green400);
    border-color: var(--greenBorder);
  `,
  error: css`
    background: var(--redBackground);
    color: var(--red400);
    border-color: var(--redBorder);
  `,
} as const;

/**
 * Shared "N unsaved changes" pill — metrics (font-size/weight/family, padding, radius) match
 * `spotter`'s own trigger badge exactly (`SpotterHeaderChanges`'s former hand-rolled `Trigger`),
 * now the one place both that component and AB Vault/manager's changes-panel banner
 * (`CandidateAssignmentsScreen`'s former `ChangesBanner`) get their height/spacing from, instead
 * of two near-identical hand-tuned copies.
 *
 * A plain `styled.span` by default — call sites that need real button semantics/click behavior
 * (Spotter's header trigger, which opens a dropdown) pass `as="button"` (styled-components'
 * built-in polymorphic prop); call sites that are just a status readout (Vault's changes panel)
 * render it as-is.
 */
const ChangesTrigger = styled.span<{ $tone: ChangesTriggerTone }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  font-family: ui-monospace, Menlo, monospace;
  padding: 6px 10px;
  border-radius: 99px;
  border: 1px solid;
  white-space: nowrap;
  ${(props) => TONE_STYLES[props.$tone]}
`;

export default ChangesTrigger;
